import { useMemo, useEffect } from 'react'

// Stores
import { useResourcesStore, useResourcesStoreActions } from '@/stores/resources'
import { useUiStoreActions } from '@/stores/ui'
import { useRoomStore, useRoomStoreActions } from '@/stores/room'

// Hooks
import { useSimpleSearch } from '@/hooks/useSimpleSearch'

// Components
import { Search } from '@/components/Search'
import { SearchEmpty } from '@/components/SearchEmpty'
import { ScrollArea } from '@/components/base/scroll-area'
import { RoomCard } from '@/components/RoomCard'

// Helpers
import type { RedirectPath, Resource } from '@/helpers/types'
import { TEST_IDS } from '@/helpers/constants'

export const PanelRoomNavigator = () => {
  const redirect = useRoomStore(state => state.redirect)
  const { setCallState } = useRoomStoreActions()
  const { setRoomPanelTitle } = useUiStoreActions()
  const { fetchResources, getResourcesFromMap } = useResourcesStoreActions()

  const roomName = useRoomStore(state => state.roomName)
  const resourcesByIdMap = useResourcesStore(state => state.resourcesByIdMap)
  const resources = getResourcesFromMap(resourcesByIdMap)

  // Get the video rooms and filter out the current room
  const rooms = resources.filter(
    room =>
      room.type === 'room' && room.channels.video && room.name !== roomName,
  )

  useEffect(() => {
    // NOTE: There is a bug, if the room is loaded without
    // viewing the RoomsView, only the loaded room
    // will show up in the list.
    // TODO: This can be modified later to auto refresh every
    // few X MS via setInterval. Ideally the search would be
    // done via API Request
    // TODO: We might be able to use the logic in RoomsView.tsx
    if (rooms.length <= 1) {
      void fetchResources()
    }
  }, [resources, rooms, fetchResources])

  const roomsSortedByDisplayName = useMemo(() => {
    return rooms
      .map(room => ({
        ...room,
        displayNameSortable: room.displayName.toLowerCase(),
      }))
      .sort((a, b) =>
        a.displayNameSortable.localeCompare(b.displayNameSortable),
      )
  }, [rooms])

  const { handleSearch, searchTerm, handleSearchReset, searchResults } =
    useSimpleSearch({
      items: roomsSortedByDisplayName,
      key: 'displayNameSortable',
    })
  const roomCount = searchTerm ? searchResults.length : rooms.length

  useEffect(() => {
    // Update the navigator panel title with the room count
    setRoomPanelTitle(`Change Rooms (${roomCount})`)
  }, [setRoomPanelTitle, roomCount])

  // TODO: Implement this when transfers are available in the SDK
  // const transferHandler = () => {
  //   console.log('transferHandler')
  // }

  const onJoin = () => {
    setCallState('switching')
  }

  return (
    <div
      className="flex h-full flex-col"
      data-testid={TEST_IDS.ROOM_PANEL_NAVIGATOR}
    >
      <div className="p-4 pl-0 pt-1">
        <Search
          formDescription="Search for a room"
          onChange={handleSearch}
          onSubmit={handleSearch}
          onReset={handleSearchReset}
          placeholder="Find a room"
        />
      </div>
      <ScrollArea className="h-full w-full pr-4">
        <ul
          aria-label="List of available rooms"
          className="grid grid-cols-1 gap-x-3 gap-y-4"
        >
          <RenderRoomCards
            redirect={redirect}
            onJoin={onJoin}
            rooms={searchTerm ? searchResults : roomsSortedByDisplayName}
            searchTerm={searchTerm}
          />
        </ul>
      </ScrollArea>
    </div>
  )
}

interface RenderRoomCardsProps {
  onJoin: () => void
  redirect: RedirectPath
  rooms: Resource[]
  searchTerm?: string
}
const RenderRoomCards = ({
  onJoin,
  rooms,
  redirect,
  searchTerm,
}: RenderRoomCardsProps) => {
  if (!rooms?.length) {
    return (
      <li>
        <SearchEmpty searchTerm={searchTerm} showIcon={false} />
      </li>
    )
  }
  return rooms?.map((room, index) => (
    <li key={room.id} className="flex flex-col">
      <RoomCard
        redirect={redirect}
        onJoin={onJoin}
        // onTransfer={transferHandler}
        imgLoad={index > 3 ? 'lazy' : 'eager'}
        room={room}
      />
    </li>
  ))
}
